import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCheck, XCircle, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Nursery","LKG","UKG","1","2","3","4","5","6","7","8","9","10","11","12"];

export default function Attendance() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState("students");
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthReport, setMonthReport] = useState([]);
  const [date, setDate] = useState(today);
  const [cls, setCls] = useState("1");
  const [students, setStudents] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [staff, setStaff] = useState([]);
  const [staffStatuses, setStaffStatuses] = useState({});
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [lv, setLv] = useState({ from_date: today, to_date: today, reason: "" });

  const loadMonthReport = async () => {
    try {
      const { data } = await api.get("/attendance/month", { params: { month: reportMonth, class_name: cls } });
      setMonthReport(data);
    } catch (e) { setMonthReport([]); }
  };
  useEffect(() => { if (tab === "report") loadMonthReport(); }, [reportMonth, cls, tab]); // eslint-disable-line

  const load = async () => {
    const [s, a] = await Promise.all([
      api.get("/students", { params: { class_name: cls } }),
      api.get("/attendance", { params: { date, class_name: cls } }),
    ]);
    setStudents(s.data);
    const map = {};
    (a.data.entries || []).forEach((e) => { map[e.student_id] = e.status; });
    setStatuses(map);
  };
  const loadStaff = async () => {
    try {
      const { data } = await api.get("/users");
      const st = data.filter(u => ["teacher","staff","school_admin"].includes(u.role));
      setStaff(st);
      const { data: att } = await api.get("/staff-attendance", { params: { date } });
      const map = {};
      (att.entries || []).forEach((e) => { map[e.user_id] = e.status; });
      setStaffStatuses(map);
    } catch (err) {
      // teachers may not have access to /users; degrade to self-only
      if (user) setStaff([{ id: user.id, name: user.name, role: user.role }]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, cls]);
  useEffect(() => { if (tab === "staff") loadStaff(); /* eslint-disable-next-line */ }, [tab, date]);

  const setSt = (id, v) => setStatuses({ ...statuses, [id]: v });
  const setStaffSt = (id, v) => setStaffStatuses({ ...staffStatuses, [id]: v });

  const markAll = (status) => {
    const next = {};
    students.forEach(s => { next[s.id] = status; });
    setStatuses(next);
    toast.success(`Marked all as ${status}`);
  };
  const markAllStaff = (status) => {
    const next = {};
    staff.forEach(s => { next[s.id] = status; });
    setStaffStatuses(next);
    toast.success(`Marked all staff as ${status}`);
  };

  const save = async () => {
    try {
      const entries = students.map((s) => ({ student_id: s.id, status: statuses[s.id] || "absent" }));
      await api.post("/attendance", { date, class_name: cls, entries });
      toast.success("Attendance saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveStaff = async () => {
    try {
      const entries = staff.map((s) => ({ user_id: s.id, user_name: s.name, role: s.role, status: staffStatuses[s.id] || "absent" }));
      await api.post("/staff-attendance", { date, entries });
      toast.success("Staff attendance saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markSelf = async (status) => {
    try {
      await api.post("/staff-attendance/self", { date, status });
      toast.success(`You are marked ${status}`);
      loadStaff();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const applyLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leaves", { ...lv, applicant_type: user?.role === "parent" ? "parent" : "teacher" });
      toast.success("Leave applied");
      setLeaveOpen(false);
      setLv({ from_date: today, to_date: today, reason: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const isAdmin = user?.role === "school_admin" || user?.role === "super_admin";

  return (
    <DashLayout variant="school" title="Attendance">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-1 overflow-x-auto pb-1 mb-4">
          <TabsList className="inline-flex w-max min-w-full">
            <TabsTrigger value="students" data-testid="tab-att-students">Student Attendance</TabsTrigger>
            <TabsTrigger value="staff" data-testid="tab-att-staff">{isAdmin ? "Staff Attendance" : "My Attendance"}</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-att-report">Monthly Report</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="report">
          <Card className="p-4 flex flex-wrap gap-3 items-end mb-3">
            <div><Label className="text-xs">Month</Label><Input type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="w-40" data-testid="att-report-month" /></div>
            <div><Label className="text-xs">Class</Label>
              <Select value={cls} onValueChange={setCls}>
                <SelectTrigger className="w-36" data-testid="att-report-class"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={loadMonthReport} data-testid="att-report-refresh">Refresh</Button>
          </Card>
          <Card className="p-0 overflow-hidden" data-testid="att-report-card">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-slate-50 border-b">
                <tr><th className="text-left p-3">Student</th><th className="text-right p-3">Days Marked</th><th className="text-right p-3">Present</th><th className="text-right p-3">Absent</th><th className="text-right p-3">Late</th><th className="text-right p-3">%</th></tr>
              </thead>
              <tbody>
                {monthReport.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No records for {reportMonth}. Run daily attendance first.</td></tr>}
                {monthReport.map((r) => (
                  <tr key={r.student_id} className="border-b last:border-0" data-testid={`report-row-${r.student_id}`}>
                    <td className="p-3 font-semibold">{r.name}</td>
                    <td className="p-3 text-right tabular-nums">{r.marked}</td>
                    <td className="p-3 text-right tabular-nums text-emerald-700">{r.present}</td>
                    <td className="p-3 text-right tabular-nums text-red-700">{r.absent}</td>
                    <td className="p-3 text-right tabular-nums text-amber-700">{r.late}</td>
                    <td className="p-3 text-right font-bold tabular-nums">{r.percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="students">
          <Card className="p-4 flex flex-wrap items-end gap-4 mb-6">
            <div><Label className="mb-1 block text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date" /></div>
            <div><Label className="mb-1 block text-xs">Class</Label>
              <Select value={cls} onValueChange={setCls}>
                <SelectTrigger className="w-32" data-testid="att-class"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="flex gap-2 ml-auto flex-wrap">
              <Button variant="outline" onClick={() => markAll("present")} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="att-mark-all-present">
                <CheckCheck className="w-4 h-4 mr-1" />All Present
              </Button>
              <Button variant="outline" onClick={() => markAll("absent")} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="att-mark-all-absent">
                <XCircle className="w-4 h-4 mr-1" />All Absent
              </Button>
              <Button onClick={save} className="bg-blue-700 hover:bg-blue-800" data-testid="att-save">Save Attendance</Button>
            </div>
          </Card>

          <div className="bg-white rounded-lg border border-slate-200 divide-y">
            {students.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-4" data-testid={`att-row-${s.id}`}>
                <div><div className="font-semibold">{s.first_name} {s.last_name}</div><div className="text-xs text-slate-500">{s.registration_no}</div></div>
                <div className="flex gap-1">
                  {["present", "absent", "late"].map((v) => (
                    <Button key={v} size="sm" variant={statuses[s.id] === v ? "default" : "outline"}
                      className={statuses[s.id] === v ? (v === "present" ? "bg-emerald-600 hover:bg-emerald-700" : v === "absent" ? "bg-red-600 hover:bg-red-700" : "bg-amber-500 hover:bg-amber-600") : ""}
                      onClick={() => setSt(s.id, v)} data-testid={`att-${v}-${s.id}`}>{v}</Button>
                  ))}
                </div>
              </div>
            ))}
            {students.length === 0 && <div className="p-8 text-center text-slate-500">No students in this class</div>}
          </div>
        </TabsContent>

        <TabsContent value="staff">
          <Card className="p-4 flex flex-wrap items-end gap-4 mb-6">
            <div><Label className="mb-1 block text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="staff-att-date" /></div>
            <div className="ml-auto flex gap-2 flex-wrap">
              {!isAdmin && (
                <>
                  <Button variant="outline" onClick={() => markSelf("present")} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="staff-att-self-present">
                    <CheckCheck className="w-4 h-4 mr-1" />Mark Myself Present
                  </Button>
                  <Button variant="outline" onClick={() => markSelf("absent")} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="staff-att-self-absent">
                    <XCircle className="w-4 h-4 mr-1" />Mark Myself Absent
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={() => setLeaveOpen(true)} data-testid="staff-att-apply-leave">
                <CalendarClock className="w-4 h-4 mr-1" />Apply for Leave
              </Button>
              {isAdmin && (
                <>
                  <Button variant="outline" onClick={() => markAllStaff("present")} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="staff-att-all-present">
                    <CheckCheck className="w-4 h-4 mr-1" />All Present
                  </Button>
                  <Button variant="outline" onClick={() => markAllStaff("absent")} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="staff-att-all-absent">
                    <XCircle className="w-4 h-4 mr-1" />All Absent
                  </Button>
                  <Button onClick={saveStaff} className="bg-blue-700 hover:bg-blue-800" data-testid="staff-att-save">Save</Button>
                </>
              )}
            </div>
          </Card>

          <div className="bg-white rounded-lg border border-slate-200 divide-y">
            {staff.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-4" data-testid={`staff-att-row-${s.id}`}>
                <div>
                  <div className="font-semibold">{s.name} {s.id === user?.id && <span className="text-xs text-blue-600 ml-2">(You)</span>}</div>
                  <div className="text-xs text-slate-500 capitalize">{s.role?.replace("_", " ")}</div>
                </div>
                <div className="flex gap-1">
                  {["present", "absent", "leave"].map((v) => (
                    <Button key={v} size="sm" variant={staffStatuses[s.id] === v ? "default" : "outline"}
                      disabled={!isAdmin && s.id !== user?.id}
                      className={staffStatuses[s.id] === v ? (v === "present" ? "bg-emerald-600 hover:bg-emerald-700" : v === "absent" ? "bg-red-600 hover:bg-red-700" : "bg-amber-500 hover:bg-amber-600") : ""}
                      onClick={() => setStaffSt(s.id, v)} data-testid={`staff-att-${v}-${s.id}`}>{v}</Button>
                  ))}
                </div>
              </div>
            ))}
            {staff.length === 0 && <div className="p-8 text-center text-slate-500">No staff records</div>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
          <form onSubmit={applyLeave} className="space-y-3" data-testid="quick-leave-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">From</Label><Input type="date" required value={lv.from_date} onChange={(e) => setLv({ ...lv, from_date: e.target.value })} data-testid="quick-lv-from" /></div>
              <div><Label className="mb-1 block text-xs">To</Label><Input type="date" required value={lv.to_date} onChange={(e) => setLv({ ...lv, to_date: e.target.value })} data-testid="quick-lv-to" /></div>
            </div>
            <div><Label className="mb-1 block text-xs">Reason</Label><Textarea rows={3} required value={lv.reason} onChange={(e) => setLv({ ...lv, reason: e.target.value })} data-testid="quick-lv-reason" /></div>
            <DialogFooter>
              <Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="quick-lv-submit">Apply</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
